import { FormApi } from 'final-form';
import {
  deleteUploadedFile,
  UploadedFileReference,
  uploadFile,
  UploadRequestError,
} from '../../../api/fileUpload';

export interface UploadTransfer {
  status: 'uploading' | 'uploaded' | 'failed';
  progress: number;
  reference?: UploadedFileReference;
  errorStatus?: number;
  errorCode?: string;
}
const transfers = new WeakMap<File, UploadTransfer>();
export const getUploadTransfer = (file: File) => transfers.get(file);
export const isUploaded = (file: File) => {
  const transfer = transfers.get(file);
  return (
    transfer?.status === 'uploaded' &&
    !!transfer.reference &&
    Date.parse(transfer.reference.expiresAt) > Date.now()
  );
};

interface Job {
  field: string;
  file: File;
  abort: () => void;
  removed: boolean;
  expiryTimer?: ReturnType<typeof setTimeout>;
}
const managers = new WeakMap<FormApi, Set<Job>>();
const unsubscribers = new WeakMap<FormApi, () => void>();

function removeReference(reference: UploadedFileReference) {
  // Expiry is the fallback when the client is offline or closes the application.
  deleteUploadedFile(reference.uploadId).catch(() => {});
}

function notify(form: FormApi, field: string, file: File, complete: boolean) {
  const current = form.getState().values[field];
  if (!Array.isArray(current) || !current.includes(file)) return;
  const metadata = form.getFieldState(field)?.data;
  form.batch(() => {
    if (complete) {
      const value = [...current];
      const snapshot = metadata?.uploadSelection;
      if (snapshot?.value === current)
        form.mutators.setFieldData?.(field, {
          uploadSelection: { ...snapshot, value },
        });
      form.change(field, value);
    }
    form.mutators.setFieldData?.(field, {
      uploadRevision: (metadata?.uploadRevision ?? 0) + 1,
    });
  });
}

// Transfers belong to the form, so changing pages does not cancel an upload.
// A form-wide value subscription also cleans up removals/resets on other pages.
function jobsFor(form: FormApi): Set<Job> {
  const existing = managers.get(form);
  if (existing) return existing;
  const jobs = new Set<Job>();
  managers.set(form, jobs);
  const unsubscribe = form.subscribe(
    ({ values }) => {
      jobs.forEach((job) => {
        if (
          Array.isArray(values[job.field]) &&
          values[job.field].includes(job.file)
        )
          return;
        job.removed = true;
        if (job.expiryTimer) clearTimeout(job.expiryTimer);
        job.abort();
        const reference = transfers.get(job.file)?.reference;
        if (reference) removeReference(reference);
        transfers.delete(job.file);
        jobs.delete(job);
      });
    },
    { values: true }
  );
  unsubscribers.set(form, unsubscribe);
  return jobs;
}

export function startUpload(
  form: FormApi,
  field: string,
  file: File,
  configVersion: string,
  project: string
) {
  const jobs = jobsFor(form);
  if (Array.from(jobs).some((job) => job.file === file && job.field === field))
    return;
  const job: Job = { field, file, removed: false, abort: () => {} };
  jobs.add(job);
  const restored = transfers.get(file);
  if (restored) {
    if (restored.reference) scheduleExpiry(job, restored.reference, form);
    return;
  }
  transfers.set(file, { status: 'uploading', progress: 0 });
  const request = uploadFile(
    file,
    configVersion,
    project,
    field,
    (progress) => {
      if (job.removed) return;
      transfers.set(file, { status: 'uploading', progress });
      notify(form, field, file, false);
    }
  );
  job.abort = request.abort;
  notify(form, field, file, false);
  request.promise
    .then((reference) => {
      if (job.removed) {
        removeReference(reference);
        return;
      }
      transfers.set(file, { status: 'uploaded', progress: 100, reference });
      notify(form, field, file, true);
      scheduleExpiry(job, reference, form);
    })
    .catch((error) => {
      if (job.removed) return;
      transfers.set(file, {
        status: 'failed',
        progress: 0,
        errorStatus: error instanceof UploadRequestError ? error.status : 0,
        errorCode: error instanceof UploadRequestError ? error.code : undefined,
      });
      notify(form, field, file, true);
    });
}

function scheduleExpiry(
  job: Job,
  reference: UploadedFileReference,
  form: FormApi
) {
  const remaining = Date.parse(reference.expiresAt) - Date.now();
  if (remaining <= 0) return;
  job.expiryTimer = setTimeout(() => {
    if (job.removed) return;
    if (Date.parse(reference.expiresAt) > Date.now())
      scheduleExpiry(job, reference, form);
    else notify(form, job.field, job.file, true);
  }, Math.min(2147483647, remaining + 1));
}

export function disposeUploads(form: FormApi) {
  unsubscribers.get(form)?.();
  unsubscribers.delete(form);
  managers.get(form)?.forEach((job) => {
    job.removed = true;
    if (job.expiryTimer) clearTimeout(job.expiryTimer);
    if (transfers.get(job.file)?.status === 'uploading') {
      job.abort();
      transfers.set(job.file, {
        status: 'failed',
        progress: 0,
        errorStatus: 0,
      });
    }
  });
  managers.delete(form);
}

// Keep completed references across the existing session-storage reload flow.
// Raw file bytes and incomplete transfers cannot be restored after a reload.
export function serializeUploadValues(
  values: Record<string, any>
): Record<string, any> {
  return Object.fromEntries(
    Object.entries(values).map(([field, value]) => [
      field,
      Array.isArray(value)
        ? value.map((file) => {
            if (!(file instanceof File)) return file;
            const reference = transfers.get(file)?.reference;
            return reference
              ? { ...reference, uploadReference: true }
              : {
                  uploadPending: true,
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  lastModified: file.lastModified,
                };
          })
        : value,
    ])
  );
}

export function restoreUploadValues(
  values: Record<string, any>
): Record<string, any> {
  return Object.fromEntries(
    Object.entries(values).map(([field, value]) => [
      field,
      Array.isArray(value)
        ? value.map((stored) => {
            if (!stored?.uploadReference && !stored?.uploadPending)
              return stored;
            const file = new File([], stored.name, {
              type: stored.type,
              lastModified: stored.lastModified,
            });
            Object.defineProperty(file, 'size', { value: stored.size });
            if (stored.uploadReference) {
              const { uploadReference, ...reference } = stored;
              transfers.set(file, {
                status: 'uploaded',
                progress: 100,
                reference,
              });
            } else
              transfers.set(file, {
                status: 'failed',
                progress: 0,
                errorStatus: 0,
              });
            return file;
          })
        : value,
    ])
  );
}
